import React from 'react';
import { Box, FormControl, InputLabel, Select, MenuItem, TextField, ToggleButton, ToggleButtonGroup } from '@mui/material';
import { FilterAlt, FilterAltOff } from '@mui/icons-material';
import type { ReceiptAuditsParams } from '../../services/auditService';
import { Store } from '../../services/authService';
import SelectDatePicker from './SelectDatePicker';

const DEFAULT_AGE_DAYS = 7;

type AuditFilterBarProps = {
  stores: Store[];
  storesLoading: boolean;
  pendingStoreFilter: string;
  pendingStatusFilter: string;
  pendingReportTypeFilter: string;
  pendingDateRange: [Date | null, Date | null];
  pendingAgeDays: number;
  allowedStatuses: ReceiptAuditsParams['status'][];
  setPendingStoreFilter: (value: string) => void;
  setPendingStatusFilter: (value: string) => void;
  setPendingReportTypeFilter: (value: string) => void;
  setPendingDateRange: (range: [Date | null, Date | null]) => void;
  setPendingAgeDays: (value: number) => void;
  setFilters: (filters: any) => void;
  updateURLParams: (filters: any) => void;
  onClear: () => void;
};

const AuditFilterBar: React.FC<AuditFilterBarProps> = ({
  stores,
  storesLoading,
  pendingStoreFilter,
  pendingStatusFilter,
  pendingReportTypeFilter,
  pendingDateRange,
  pendingAgeDays,
  allowedStatuses,
  setPendingStoreFilter,
  setPendingStatusFilter,
  setPendingReportTypeFilter,
  setPendingDateRange,
  setPendingAgeDays,
  setFilters,
  updateURLParams,
  onClear,
}) => {
  const formatDateForAPI = (date: Date) => {
    const year = date.getFullYear();
    const month = String(date.getMonth() + 1).padStart(2, '0');
    const day = String(date.getDate()).padStart(2, '0');
    return `${year}-${month}-${day}`;
  };

  const primaryStatusOptions = [
    { value: 'None', label: 'All' },
    { value: 'pending', label: 'Pending' },
    { value: 'processed', label: 'Processed' },
  ];

  const exactStatusOptions = [
    { value: 'queued', label: 'Queued' },
    { value: 'processing', label: 'Processing' },
    { value: 'ready', label: 'Ready' },
    { value: 'finalized', label: 'Finalized' },
    { value: 'auto_created', label: 'Auto Created' },
    { value: 'auto_finalized', label: 'Auto Finalized' },
    { value: 'failed', label: 'Failed' },
    { value: 'rejected', label: 'Rejected' },
  ];

  const reportTypeOptions: { value: NonNullable<ReceiptAuditsParams['report_type']>; label: string }[] = [
    { value: 'ticket_balance', label: 'Receipt Audit' },
    { value: 'vending_sales', label: 'Vending Sales' },
    { value: 'vending_inventory', label: 'Vending Inventory' },
    { value: 'vending_shift_report', label: 'Vending Shift Report' },
    { value: 'draw_games_daily', label: 'Draw Games Daily' },
    { value: 'sciq_ticket_audit', label: 'Sciq Ticket Audit' },
  ];

  const storeLabelFor = (store: Store) => [store.name, store.address_line1].filter(Boolean).join(' - ');
  const isPrimaryStatus = primaryStatusOptions.some((option) => option.value === pendingStatusFilter);
  const exactStatusValue = isPrimaryStatus ? '' : pendingStatusFilter;

  const applyFilters = ({
    storeId = pendingStoreFilter,
    status = pendingStatusFilter,
    reportType = pendingReportTypeFilter,
    dateRange = pendingDateRange,
    ageDays = pendingAgeDays,
  }: {
    storeId?: string;
    status?: string;
    reportType?: string;
    dateRange?: [Date | null, Date | null];
    ageDays?: number;
  }) => {
    const filterObj: any = { age_days: ageDays };
    if (storeId) filterObj.store_id = storeId;
    if (allowedStatuses.includes(status as any) && status !== 'None') {
      filterObj.status = status as ReceiptAuditsParams['status'];
    }
    if (reportType) {
      filterObj.report_type = reportType as ReceiptAuditsParams['report_type'];
    }
    if (dateRange[0]) filterObj.date_from = formatDateForAPI(dateRange[0]);
    if (dateRange[1]) filterObj.date_to = formatDateForAPI(dateRange[1]);

    setFilters(filterObj);
    const urlFilters: any = { ...filterObj };
    if (status) urlFilters.status = status;
    updateURLParams(urlFilters);
  };

  return (
    <Box
      sx={{
        alignItems: 'center',
        display: 'flex',
        flexWrap: { xs: 'wrap', lg: 'nowrap' },
        gap: 1.5, 
        width: '100%',
        '& > *': { flexShrink: 1 },
        '& .MuiInputBase-root, & .MuiInputBase-input, & .MuiSelect-select': {
          fontSize: '14px',
        },
        '& .MuiInputLabel-root': {
          fontSize: '14px',
        },
      }}
    >
      {(pendingStoreFilter || (pendingStatusFilter && pendingStatusFilter !== 'None') || pendingReportTypeFilter || (pendingDateRange[0] && pendingDateRange[1])) ? (
        <FilterAltOff
          sx={{ color: 'primary.main', mr: 1, cursor: 'pointer' }}
          onClick={onClear}
        />
      ) : (
        <FilterAlt sx={{ color: 'primary.main', mr: 1 }} />
      )}
      {/* Store Filter */}
      <FormControl
        size="small"
        sx={{
          minWidth: 280,
          maxWidth: 420,
          width: 280,
          '& .MuiOutlinedInput-root': { height: 36 },
        }}
      >
        <InputLabel>Store</InputLabel>
        <Select
          value={pendingStoreFilter}
          label="Store"
          sx={{ '& .MuiSelect-select': { padding: '10px 12px !important' } }}
          onChange={(e) => {
            const value = e.target.value as string;
            setPendingStoreFilter(value);
            applyFilters({ storeId: value });
          }}
          disabled={storesLoading}
          MenuProps={{
            PaperProps: {
              sx: {
                width: 520,
                maxWidth: 'min(520px, calc(100vw - 32px))',
              },
            },
          }}
          renderValue={(selected) => {
            if (!selected) return 'None';
            const store = stores.find(s => s.id === selected);
            const label = store ? storeLabelFor(store) : selected as string;
            return (
              <Box sx={{
                overflow: 'hidden',
                textOverflow: 'ellipsis',
                whiteSpace: 'nowrap',
                display: 'block',
              }}>{label}</Box>
            );
          }}
        >
          <MenuItem value="">None</MenuItem>
          {stores.map((store) => (
            <MenuItem key={store.id} value={store.id} sx={{ whiteSpace: 'nowrap', minWidth: 0 }}>
              <Box sx={{ overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap', width: '100%' }}>
                {storeLabelFor(store)}
              </Box>
            </MenuItem>
          ))}
        </Select>
      </FormControl>
      <ToggleButtonGroup
        exclusive
        size="small"
        value={isPrimaryStatus ? pendingStatusFilter : null}
        onChange={(_, value) => {
          if (!value) return;
          setPendingStatusFilter(value);
          if (value === 'pending') {
            setPendingAgeDays(0);
            applyFilters({ status: value, ageDays: 0 });
            return;
          }
          if (value === 'processed') {
            setPendingAgeDays(DEFAULT_AGE_DAYS);
            applyFilters({ status: value, ageDays: DEFAULT_AGE_DAYS });
            return;
          }
          if (value === 'None') {
            setPendingAgeDays(DEFAULT_AGE_DAYS);
            applyFilters({ status: value, ageDays: DEFAULT_AGE_DAYS });
            return;
          }
          applyFilters({ status: value });
        }}
        sx={{
          height: 36,
          '& .MuiToggleButton-root': {
            flex: 1,
            px: 1.5,
            color: '#2B3445',
            fontWeight: 700,
            fontSize: '14px',
            letterSpacing: 0,
            textTransform: 'none',
            borderColor: '#D6DBE1',
          },
          '& .Mui-selected': {
            color: '#ffffff !important',
            backgroundColor: '#2C6CA0 !important',
          },
        }}
      >
        {primaryStatusOptions.map((option) => (
          <ToggleButton key={option.value} value={option.value}>
            {option.label}
          </ToggleButton>
        ))}
      </ToggleButtonGroup>
      <FormControl
        size="small"
        sx={{
          minWidth: 140,
          '& .MuiOutlinedInput-root': { height: 36 },
        }}
      >
        <Select
          value={exactStatusValue}
          displayEmpty
          renderValue={(selected) => selected
            ? exactStatusOptions.find((option) => option.value === selected)?.label || selected
            : 'Status'
          }
          sx={{
            backgroundColor: '#F8FAFC',
            '& .MuiSelect-select': { padding: '10px 12px !important' },
            '& .MuiOutlinedInput-notchedOutline': { borderColor: '#DDE3EA' },
            '&:hover .MuiOutlinedInput-notchedOutline': { borderColor: '#B8C2CC' },
            '&.Mui-focused .MuiOutlinedInput-notchedOutline': { borderColor: '#8FA4B8' },
          }}
          onChange={(e) => {
            const value = e.target.value as string;
            setPendingStatusFilter(value);
            applyFilters({ status: value });
          }}
        >
          {exactStatusOptions.map((option) => (
            <MenuItem key={option.value} value={option.value}>
              {option.label}
            </MenuItem>
          ))}
        </Select>
      </FormControl>
      <FormControl
        size="small"
        sx={{
          minWidth: 190,
          '& .MuiOutlinedInput-root': { height: 36 },
        }}
      >
        <InputLabel>Report Type</InputLabel>
        <Select
          value={pendingReportTypeFilter}
          label="Report Type"
          sx={{ '& .MuiSelect-select': { padding: '10px 12px !important' } }}
          onChange={(e) => {
            const value = e.target.value as string;
            setPendingReportTypeFilter(value);
            applyFilters({ reportType: value });
          }}
        >
          <MenuItem value="">None</MenuItem>
          {reportTypeOptions.map((option) => (
            <MenuItem key={option.value} value={option.value}>
              {option.label}
            </MenuItem>
          ))}
        </Select>
      </FormControl>
      {/* Date Range Filter */}
      <Box sx={{ height: 36, display: 'flex', alignItems: 'center', maxWidth: 220, minWidth: 0, width: 220 }}>
        <SelectDatePicker
          value={pendingDateRange}
          onChange={(update: [Date | null, Date | null]) => {
            setPendingDateRange(update);
            if (update[0] && update[1]) {
              applyFilters({ dateRange: update });
            } else if (!update[0] && !update[1]) {
              applyFilters({ dateRange: update });
            }
          }}
        />
      </Box>
      <TextField
        label="Age days"
        type="number"
        size="small"
        value={pendingAgeDays}
        sx={{ width: 100, '& .MuiOutlinedInput-root': { height: 36 }, '& input': { padding: '10px 12px' } }}
        inputProps={{ min: 0 }}
        onChange={(event) => {
          const nextAgeDays = Math.max(0, Number.parseInt(event.target.value || '0', 10) || 0);
          setPendingAgeDays(nextAgeDays);
          applyFilters({ ageDays: nextAgeDays });
        }}
      />
    </Box>
  );
};

export default AuditFilterBar;
